import React, { useState } from "react";
import { FullScreenModal, Input, Button, Select } from "./ui";
import { useServices } from "../hooks/useServices";
import { useCreateServiceRequest } from "../hooks/useServiceRequests";
import type { SelectOption } from "./ui/Select";

const PRIORITY_OPTIONS: SelectOption[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export interface CreateServiceRequestModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateServiceRequestModal({
  open,
  onClose,
}: CreateServiceRequestModalProps) {
  const { data: services = [], isLoading: servicesLoading } = useServices({
    isActive: true,
  });
  const createMutation = useCreateServiceRequest();

  const [serviceId, setServiceId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [priority, setPriority] = useState("medium");
  const [error, setError] = useState("");

  const serviceOptions: SelectOption[] = services.map((s) => ({
    value: s._id,
    label: `${s.name} (${s.category}) - ₹${s.basePrice ?? 0}`,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!serviceId) {
      setError("Please select a service");
      return;
    }
    try {
      await createMutation.mutateAsync({
        serviceId,
        customerDetails:
          customerName || customerPhone
            ? { name: customerName || undefined, phone: customerPhone || undefined }
            : undefined,
        priority: priority as any,
      });
      setServiceId("");
      setCustomerName("");
      setCustomerPhone("");
      setPriority("medium");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create request");
    }
  };

  return (
    <FullScreenModal
      isOpen={open}
      onClose={onClose}
      title="Create Service Request"
    >
      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Service"
            options={serviceOptions}
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            placeholder={servicesLoading ? "Loading..." : "Select a service"}
            required
            disabled={servicesLoading}
          />
          <Input
            label="Customer Name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Customer name"
          />
          <Input
            label="Customer Phone (optional)"
            type="tel"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="10-digit mobile"
          />
          <Select
            label="Priority"
            options={PRIORITY_OPTIONS}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending}
              disabled={createMutation.isPending}
              className="flex-1"
            >
              Create Request
            </Button>
          </div>
        </form>
      </div>
    </FullScreenModal>
  );
}
